"use client"

import React, { useEffect, useState } from "react";
import { getDatabase, ref, onValue, update, remove } from "firebase/database";
import { FaTrash } from "react-icons/fa";
import { useAccount } from "../context/AccountContext";
import "./AltAlarm.css";

const AltAlarm = () => {
  const { currentAccount, accountSwitchTrigger } = useAccount();
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasNewAlarm, setHasNewAlarm] = useState(false);

  // Fetch alarms for current account
  useEffect(() => {
    if (!currentAccount) {
      setAlarms([]);
      setLoading(false);
      return;
    }

    if (!navigator.onLine) {
      setError("You are offline. Please check your internet connection.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    const db = getDatabase();
    const alarmsRef = ref(db, `alt_alarm/${currentAccount.uid}`);

    const unsubscribe = onValue(
      alarmsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const alarmsData = snapshot.val();
          const alarmList = Object.entries(alarmsData).map(([alarmId, alarm]) => ({
            alarmId,
            ...alarm,
          }));
          setAlarms(alarmList);
          setHasNewAlarm(alarmList.some((alarm) => alarm.isNew));
        } else {
          setAlarms([]);
          setHasNewAlarm(false);
        }
        setLoading(false);
      },
      (err) => {
        setError("Failed to load alarms: " + err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentAccount, accountSwitchTrigger]);

  // Delete alarm
  const deleteAlarm = async (alarmId) => {
    try {
      if (!currentAccount) return;
      const db = getDatabase();
      const alarmRef = ref(db, `alt_alarm/${currentAccount.uid}/${alarmId}`);
      await remove(alarmRef);
      setAlarms((prev) => prev.filter((alarm) => alarm.alarmId !== alarmId));
    } catch (err) {
      setError("Failed to delete alarm: " + err.message);
    }
  };

  // Clear new alarms
  const clearNewAlarms = async () => {
    if (!currentAccount) return;
    const db = getDatabase();
    const alarmsRef = ref(db, `alt_alarm/${currentAccount.uid}`);
    const updates = {};
    alarms.forEach((alarm) => {
      updates[`${alarm.alarmId}/isNew`] = false;
    });
    await update(alarmsRef, updates);
    setHasNewAlarm(false);
  };

  if (!currentAccount) {
    return (
      <p className="text-center mt-5 text-danger fw-bold">
        Please login or select an account to view alarms.
      </p>
    );
  }

  if (loading) return <p className="text-center mt-5 fw-semibold">Loading alarms...</p>;

  if (error)
    return <p className="error-message text-danger fw-bold">{error}</p>;

  if (alarms.length === 0) {
    return (
      <p className="text-center mt-5 text-danger fw-semibold">
        No alarms available for your account.
      </p>
    );
  }

  return (
    <div className="container-fluid Alarm-cont">
      <div className="alarm-dashboard">
        <h2 className="alarm-Text">Your Alerts</h2>

        {hasNewAlarm && (
          <button onClick={clearNewAlarms} className="btn btn-warning mb-3">
            Clear New Alarms
          </button>
        )}

        {alarms.map((alarm) => (
          <div
            key={alarm.alarmId}
            className="alarm-card d-flex justify-content-between align-items-start"
            style={{
              borderLeftColor: alarm.isNew ? "#dc3545" : "#ffc107",
              backgroundColor: alarm.isNew ? "#ffe2e2" : "#fff3cd",
            }}
          >
            <div>
              <p>
                <strong>Issue:</strong> {alarm.type || "N/A"}
              </p>
              <p>
                <strong>Reason:</strong> {alarm.reason || "N/A"}
              </p>
              <p>
                <strong>Message:</strong> {alarm.message || "No message provided."}
              </p>
              <p>
                <strong>Last Updated:</strong>{" "}
                {alarm.lastUpdated
                  ? new Date(alarm.lastUpdated).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                      timeZone: "Asia/Kolkata",
                    })
                  : "N/A"}
              </p>
              {alarm.isNew && <p className="new-alert">🆕 This is a new alert</p>}
            </div>

            <button
              className="btn btn-sm btn-danger ms-3"
              onClick={() => deleteAlarm(alarm.alarmId)}
              title="Delete Alarm"
            >
              <FaTrash />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AltAlarm;
